import { Link, useLocation, useMatches } from 'react-router-dom'
import { ChevronRight, Home } from 'lucide-react'
import { useQueryClient, useQueries } from '@tanstack/react-query'
import { getUnit } from '@/services/unit.service'
import { getInstitute } from '@/services/institute.service'
import { getTest } from '@/services/test.service'
import { getClass } from '@/services/class.service'
import { getModule } from '@/services/module.service'
import type { Unit, Institute, Question, Test, Class, Module } from '@/types'
import { richTextToPlainText } from '@/utils/formatters'
import { ROUTES } from '@/config/routes'

/** Route handle shape consumed by `<Breadcrumb>`: `{ crumb: string | ((params) => string) }`. */
export interface BreadcrumbHandle {
  crumb: string | ((data?: unknown) => string)
}

/**
 * Automatic breadcrumb with ID → human-readable name resolution.
 *
 * Segments that look like Firestore IDs (20+ chars) are resolved first from the
 * TanStack Query cache, and fetched from Firestore when the cache is empty
 * (e.g. on hard refresh). Zero-overhead: only units and institutes are fetched
 * because those are the most common IDs that appear in the URL.
 */
export function Breadcrumb() {
  const location = useLocation()
  const matches = useMatches()
  const qc = useQueryClient()

  // Collect unique IDs that appear in the URL alongside their collection hint
  const segments = location.pathname.split('/').filter(Boolean)
  const idsToFetch: { collection: string; id: string }[] = []

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]
    if (looksLikeId(seg)) {
      const col = segments[i - 1] ?? ''
      if (col === 'units' || col === 'institutes' || col === 'tests' || col === 'classes') {
        idsToFetch.push({ collection: col, id: seg })
      }
    }
  }

  // Fire parallel queries for any IDs not already in the cache
  useQueries({
    queries: idsToFetch
      .filter(({ collection: col, id }) => {
        if (col === 'units') return !qc.getQueryData<Unit>(['units', id])
        if (col === 'institutes') return !qc.getQueryData<Institute>(['institutes', id])
        if (col === 'tests') return !qc.getQueryData<Test>(['tests', id])
        if (col === 'classes') return !qc.getQueryData<Class>(['class', id])
        if (col === 'modules') return !qc.getQueryData<Module>(['modules', id])
        return false
      })
      .map(({ collection: col, id }) => ({
        queryKey: [col, id],
        queryFn: () =>
          col === 'units' ? getUnit(id) :
          col === 'tests' ? getTest(id) :
          col === 'classes' ? getClass(id) :
          col === 'modules' ? getModule(id) :
          getInstitute(id),
        staleTime: 5 * 60 * 1000,
      })),
  })

  const crumbs = buildCrumbs(location.pathname, matches, (collection, id) =>
    resolveIdLabel(qc, collection, id)
  )

  // Deduplicate consecutive crumbs that resolve to the same path (can happen
  // when intermediate segments are not valid routes and all fall back to the
  // same ancestor). Keep the last label for the duplicated path so the most
  // descriptive name wins. Always keep Home (index 0) and the current page (last).
  const deduped = deduplicateCrumbs(crumbs)

  if (deduped.length <= 1) return null

  return (
    <nav aria-label="Breadcrumb" className="mb-4">
      <ol className="flex flex-wrap items-center gap-1 text-sm text-gray-500">
        {deduped.map((crumb, idx) => {
          const isLast = idx === deduped.length - 1
          // Use idx in key to guarantee uniqueness even if two crumbs share a path
          return (
            <li key={`${idx}-${crumb.path}`} className="flex items-center gap-1">
              {idx === 0 ? (
                <Home className="h-3.5 w-3.5 shrink-0" aria-hidden />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
              )}
              {isLast ? (
                <span className="font-medium text-gray-900 truncate max-w-[220px]">
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.path}
                  className="hover:text-primary-600 transition-colors truncate max-w-[220px]"
                >
                  {crumb.label}
                </Link>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

// ── helpers ────────────────────────────────────────────────────────────────────

interface Crumb {
  label: string
  path: string
}

/** Known static labels for URL path segments. */
const SEGMENT_LABELS: Record<string, string> = {
  admin: 'Admin',
  institutes: 'Institutes',
  units: 'Units',
  questions: 'Questions',
  answers: 'Answers',
  tests: 'Tests',
  classes: 'Classes',
  tags: 'Tags',
  student: 'Student',
  dashboard: 'Dashboard',
  enrollment: 'Enrollment',
  history: 'History',
  practice: 'Practice',
  results: 'Results',
  sections: 'Sections',
  users: 'Users',
  content: 'Content',
  analytics: 'Analytics',
  new: 'New',
  edit: 'Edit',
  modules: 'Modules'
}

/** Heuristic: Firestore auto-IDs are 20 chars; UUIDs are 36 chars. */
function looksLikeId(seg: string): boolean {
  return seg.length >= 20 || /^[a-z0-9]{8}-[a-z0-9]{4}-/.test(seg)
}

/**
 * Collapse consecutive crumbs that resolve to the same path into one entry,
 * keeping the last (most descriptive) label. Home and the final crumb are
 * always preserved regardless.
 *
 * Example: [Home, Tests, "abc123", Sections, "abc123"] where both "abc123"
 * entries point to /tests/abc123 → [Home, Tests, "abc123"].
 */
function deduplicateCrumbs(crumbs: Crumb[]): Crumb[] {
  if (crumbs.length === 0) return crumbs
  const result: Crumb[] = [crumbs[0]] // always keep Home
  for (let i = 1; i < crumbs.length; i++) {
    const prev = result[result.length - 1]
    if (crumbs[i].path === prev.path) {
      // Replace the previous entry's label with the more descriptive later one
      result[result.length - 1] = crumbs[i]
    } else {
      result.push(crumbs[i])
    }
  }
  return result
}

/**
 * Attempt to resolve a Firestore doc ID to a human label using cached query data.
 * `collection` is the URL path segment immediately before the id.
 * `allSegs` and `idx` are the full URL segments and current index for context.
 */
function resolveIdLabel(
  qc: ReturnType<typeof useQueryClient>,
  collection: string,
  id: string
): string {
  switch (collection) {
    case 'units': {
      const unit = qc.getQueryData<Unit>(['units', id])
      if (unit?.title) return unit.title
      const list = qc.getQueryData<Unit[]>(['units'])
      const found = list?.find((u) => u.id === id)
      if (found?.title) return found.title
      break
    }
    case 'institutes': {
      const inst = qc.getQueryData<Institute>(['institutes', id])
      if (inst?.name) return inst.name
      const list = qc.getQueryData<Institute[]>(['institutes'])
      const found = list?.find((i) => i.id === id)
      if (found?.name) return found.name
      break
    }
    case 'questions': {
      const tryExtract = (q: Question | undefined) => q?.content ? richTextToPlainText(q.content, 40) : ''
      const text = tryExtract(qc.getQueryData<Question>(['questions', id]))
      if (text) return text
      const list = qc.getQueryData<Question[]>(['questions'])
      const fromList = tryExtract(list?.find((q) => q.id === id))
      if (fromList) return fromList
      return 'Question'
    }
    case 'tests': {
      const t = qc.getQueryData<Test>(['tests', id])
      if (t?.title) return t.title
      const list = qc.getQueryData<Test[]>(['tests'])
      const found = list?.find((t) => t.id === id)
      if (found?.title) return found.title
      break
    }
    case 'classes': {
      const cls = qc.getQueryData<Class>(['class', id])
      if (cls?.name) return cls.name
      const list = qc.getQueryData<Class[]>(['classes'])
      const found = list?.find((c) => c.id === id)
      if (found?.name) return found.name
      return 'Class'
    }
    case 'modules': {
      const mod = qc.getQueryData<Module>(['module', id])
      if (mod?.title) return mod.title
      const list = qc.getQueryData<Module[]>(['modules'])
      const found = list?.find((m) => m.id === id)
      if (found?.title) return found.title
      return 'Module'
    }
    case 'answers':  return 'Answer'
    case 'sections': return 'Section'
  }
  return `${id.slice(0, 8)}…`
}

function buildCrumbs(
  pathname: string,
  matches: ReturnType<typeof useMatches>,
  resolveId: (collection: string, id: string) => string
): Crumb[] {
  // Try using route handles first
  const handleCrumbs = matches
    .filter((m) => (m.handle as BreadcrumbHandle | undefined)?.crumb)
    .map((m) => {
      const handle = m.handle as BreadcrumbHandle
      const label =
        typeof handle.crumb === 'function' ? handle.crumb(m.data) : handle.crumb
      return { label, path: m.pathname }
    })

  if (handleCrumbs.length > 0) {
    return [{ label: 'Home', path: '/' }, ...handleCrumbs]
  }

  // Fallback: build from pathname segments, resolving IDs
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) return [{ label: 'Home', path: '/' }]

  const crumbs: Crumb[] = [{ label: 'Home', path: '/' }]
  let accumulated = ''

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]
    accumulated += `/${seg}`

    let label: string
    if (looksLikeId(seg)) {
      const prevSeg = segments[i - 1] ?? ''
      label = resolveId(prevSeg, seg)
    } else {
      label = SEGMENT_LABELS[seg] ?? seg.charAt(0).toUpperCase() + seg.slice(1)
    }

    // Resolve to the nearest ancestor valid route if `accumulated` itself has no match
    const safePath = resolveToValidAncestor(accumulated)

    crumbs.push({ label, path: safePath })
  }

  return crumbs
}

// ── Valid-route resolution ────────────────────────────────────────────────────

/**
 * Flat list of every registered route pattern extracted from ROUTES.
 * Patterns with `:param` segments will be matched against real paths by
 * converting them to regexes.
 */
const VALID_ROUTE_PATTERNS: string[] = (() => {
  const patterns = new Set<string>()
  patterns.add('/')
  const collect = (obj: unknown) => {
    if (typeof obj === 'string') { patterns.add(obj); return }
    if (obj && typeof obj === 'object') Object.values(obj).forEach(collect)
  }
  collect(ROUTES)
  return [...patterns]
})()

/**
 * Convert a route pattern like `/tests/:testId/runner/:mode` into a RegExp
 * that matches concrete paths.
 */
function patternToRegex(pattern: string): RegExp {
  const escaped = pattern
    .replace(/[.+*?^${}()|[\]\\]/g, '\\$&') // escape regex specials (not /)
    .replace(/:[^/]+/g, '[^/]+')             // :param → one non-slash segment
  return new RegExp(`^${escaped}$`)
}

const COMPILED_PATTERNS = VALID_ROUTE_PATTERNS.map((p) => ({
  pattern: p,
  regex: patternToRegex(p),
}))

/** Return true if `path` matches any registered route pattern exactly. */
function isValidRoute(path: string): boolean {
  return COMPILED_PATTERNS.some(({ regex }) => regex.test(path))
}

/**
 * Given a concrete path, return the nearest ancestor (inclusive) that is a
 * valid registered route. Strips one segment at a time until it finds a match
 * or falls back to '/'.
 *
 * Examples:
 *   /tests/abc123/runner/admin_dry_run  →  /tests/abc123/runner/admin_dry_run  (exact match)
 *   /tests/abc123/runner               →  /tests/abc123   (runner alone not registered)
 *   /tests/abc123/sections             →  /tests/abc123   (sections alone not registered)
 */
function resolveToValidAncestor(path: string): string {
  let candidate = path
  while (candidate && candidate !== '/') {
    if (isValidRoute(candidate)) return candidate
    // Strip last segment
    const lastSlash = candidate.lastIndexOf('/')
    candidate = lastSlash > 0 ? candidate.slice(0, lastSlash) : '/'
  }
  return '/'
}
